
import React from 'react';
import { MaintenanceItem, Status, Priority } from '../../types';
import StatusBadge from '../../components/StatusBadge';
import PriorityIcon from '../../components/PriorityIcon';
import { getPriorityFromApi } from './priorityHelper';
import { formatDateAgo, isOverdue } from '../../utils/dateHelper';

/**
 * Props for the MaintenanceListItem component.
 */
interface MaintenanceListItemProps {
  item: MaintenanceItem;
  isLast: boolean; // To control the bottom border
  onViewDetails: (itemId: number) => void;
  onMarkAsCompleted: (itemId: number) => void;
}

/**
 * A component that displays a single maintenance item in a row format, for use in a list view.
 * @param {MaintenanceListItemProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered maintenance list item.
 */
const MaintenanceListItem: React.FC<MaintenanceListItemProps> = ({ item, isLast, onViewDetails, onMarkAsCompleted }) => {
  // Derive display values from the item prop.
  const priority = getPriorityFromApi(item.priority);
  const formattedDate = new Date(item.created_at).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const daysAgo = formatDateAgo(item.created_at);
  const isCompleted = item.status === Status.Completed;
  const isCritical = priority === Priority.Critical;
  const isTaskOverdue = isOverdue(item.created_at, item.status);
  const isOptimistic = item.isOptimistic;

  /**
   * Handles the click event for the 'Mark as Completed' button.
   * Stops event propagation to prevent triggering the main item's `onViewDetails` action.
   * @param {React.MouseEvent} e - The mouse event.
   */
  const handleCompleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsCompleted(item.id);
  };
  
  // Dynamically build the item's wrapper CSS classes based on its state.
  const itemClasses = `
    flex flex-col sm:flex-row sm:items-center sm:space-x-4
    ${!isLast ? 'border-b border-gray-200' : ''}
    ${isCompleted ? 'bg-green-50' : ''}
    ${isOptimistic ? 'opacity-60 animate-pulse' : ''}
    ${!isCompleted && isCritical ? 'border-l-4 border-red-500' : ''}
    ${!isCompleted && !isCritical && isTaskOverdue ? 'border-l-4 border-yellow-500' : ''}
  `;

  return (
    <div className={itemClasses}>
      {/* Main content area is a button for better accessibility and larger click target */}
      <button 
        onClick={isOptimistic ? undefined : () => onViewDetails(item.id)}
        disabled={isOptimistic}
        className={`flex-grow flex items-start space-x-4 p-4 w-full text-left transition-colors duration-200 disabled:cursor-wait ${!isCompleted ? 'hover:bg-gray-50' : ''}`}
        aria-label={`View details for ${item.title}`}
      >
        <div className="flex-shrink-0 pt-1">
          <PriorityIcon priority={priority} />
        </div>
        <div className="flex-grow min-w-0">
          <p className="text-md font-semibold text-gray-800 truncate">
            <span className="text-gray-500 mr-2">#{item.id}</span>
            {item.title}
          </p>
          <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:space-x-4 text-sm text-gray-500">
            <div className="flex items-center space-x-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <span className="truncate">Assigned to:
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                    {item.assigned_to_full_name || 'Unassigned'}
                </span>
              </span>
            </div>
            <div className="flex items-center space-x-2 mt-1 sm:mt-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{formattedDate} <span className="text-gray-400">{daysAgo}</span></span>
            </div>
          </div>
        </div>
      </button>
      
      {/* Actions and Status section */}
      <div className="flex items-center justify-end space-x-4 sm:mt-0 flex-shrink-0 pr-4 pb-4 sm:pb-0 sm:pr-4">
        {!isCompleted && !isOptimistic && (
          <button
            onClick={handleCompleteClick}
            className="p-2 rounded-full text-gray-500 hover:bg-green-100 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
            title="Mark as complete"
            aria-label={`Mark "${item.title}" as complete`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        )}
        <div className="flex items-center text-sm text-gray-500 gap-1" title={`${item.images?.length || 0} attachments`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a3 3 0 006 0V7a1 1 0 112 0v4a5 5 0 01-10 0V7a5 5 0 0110 0v4a1 1 0 11-2 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
            </svg>
            <span>{item.images?.length || 0}</span>
        </div>
        <div className="flex items-center text-sm text-gray-500 gap-1" title={`${item.messages?.length || 0} messages`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span>{item.messages?.length || 0}</span>
        </div>
        <div className="w-32 text-right">
          <StatusBadge status={item.status as Status} />
        </div>
      </div>
    </div>
  );
};

export default MaintenanceListItem;
